import { Checkbox, Table, TableBody, TableCell, TableHead, TableHeadCell, TableHeadRow, TableRow, toNumberString } from '@client/toolkit';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChangePercentage } from '../../components/change-percentage';
import { ExpanableTableWraper } from '../../components/expanable-table-wraper';
import { TableExpandableCell } from '../../components/table-expanable-cell';
import { OccupancyRatesDetailDto } from '@client/api';
import { SortDirection } from '../../components/expanable-table-wraper';
import { generatePath, useNavigate } from 'react-router-dom';
import { PortfolioPaths } from '../../../portfolio/portfolio.router';
import { MdOpenInNew } from 'react-icons/md';
import dayjs from 'dayjs';

const VACANCY_STATUS_ORDER: Record<string, number> = {
  Vacant: 0,
  VacantSoon: 1,
  Rented: 2,
};

function sortChildrenByVacancy(children: OccupancyRatesDetailDto[] | null | undefined): OccupancyRatesDetailDto[] {
  if (!children) return [];
  return [...children].sort((a, b) => {
    const aOrder = a.vacancyStatus ? (VACANCY_STATUS_ORDER[a.vacancyStatus] ?? 3) : 3;
    const bOrder = b.vacancyStatus ? (VACANCY_STATUS_ORDER[b.vacancyStatus] ?? 3) : 3;
    return aOrder - bOrder;
  });
}

export const OccupancyRateTable: React.FC<{
  data: OccupancyRatesDetailDto[];
  loading?: boolean;
  sortColumn?: string;
  sortDirection?: SortDirection;
  baseYear: number;
  onSort: (col: string, direction: SortDirection) => void;
  onSelected: (ids: Set<string>) => void;
}> = ({ data, loading, sortColumn, sortDirection, onSort, onSelected, baseYear }) => {
  const { t } = useTranslation('reporting');
  const { t: tType } = useTranslation('type');
  const navigate = useNavigate();
  const columns = [
    { key: 'buildingName', label: t('occupancyRateRow.building'), isSortable: true, className: 'w-[25%]' },
    { key: 'totalArea', label: t('occupancyRateRow.totalArea'), className: 'w-[13%]' },
    { key: 'rentedArea', label: t('occupancyRateRow.rentedArea'), className: 'w-[13%]' },
    { key: 'vacantArea', label: t('occupancyRateRow.vacantArea'), className: 'w-[13%]' },
    { key: 'baseOccupancyRate', label: t('occupancyRateRow.occupancyRate', { year: baseYear }), className: 'w-[12%]' },
    {
      key: 'previousYearOccupancyRate',
      label: t('occupancyRateRow.occupancyRate', { year: baseYear - 1 }),
      className: 'w-[12%]',
    },
    { key: 'changePercentage', label: t('occupancyRateRow.occupancyChangePercentage'), className: 'w-[10%]' },
  ];
  return (
    <ExpanableTableWraper
      loading={loading}
      onSort={onSort}
      onSelected={onSelected}
      columns={columns}
      data={data}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      renderBody={(
        data,
        selectedRows,
        expandedRows,
        handleToggleSelect,
        handleKeyDownSelect,
        handleToggleExpand,
        handleKeyDownExpand,
      ) => (
        <TableBody>
          {data.map((building) => (
            <React.Fragment key={building.id}>
              <TableRow
                className="bg-surface-elevated/40 border-b border-surface-elevated group hover:bg-surface-elevated"
                onClick={() => navigate(generatePath(PortfolioPaths.BuildingAgreements, { buildingId: building.id }))}
              >
                <TableCell className="px-2 py-2 align-top content-center">
                  <Checkbox
                    onClick={(e) => e.stopPropagation()}
                    checked={selectedRows.has(building.id)}
                    onChange={() => handleToggleSelect(building.id)}
                    tabIndex={0}
                    aria-label={t('table.selectedEntity', { entity: building.name })}
                    onKeyDown={(e) => handleKeyDownSelect(e, building.id)}
                  />
                </TableCell>
                <TableExpandableCell
                  label={building.name}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleExpand(building.id);
                  }}
                  onKeyDown={(e) => handleKeyDownExpand(e, building.id)}
                  ariaLabel={
                    expandedRows.has(building.id)
                      ? t('table.collapsedEntity', { entity: building.name })
                      : t('table.expandedEntity', { entity: building.name })
                  }
                  isExpanded={expandedRows.has(building.id)}
                  icon={<MdOpenInNew className="text-brand-primary" />}
                />
                <TableCell className="px-2 py-2 align-top whitespace-nowrap">
                  {toNumberString(building.totalM2 ?? 0)} m² / <span className="text-white/60">{building.totalSpaceCount ?? 0} {t('occupancyRateRow.spaces')}</span>
                </TableCell>
                <TableCell className="px-2 py-2 align-top whitespace-nowrap">
                  {toNumberString(building.rentedM2 ?? 0)} m² / <span className="text-white/60">{building.rentedSpaceCount ?? 0} {t('occupancyRateRow.spaces')}</span>
                </TableCell>
                <TableCell className="px-2 py-2 align-top whitespace-nowrap">
                  {toNumberString(building.vacantM2 ?? 0)} m² / <span className="text-white/60">{building.vacantSpaceCount ?? 0} {t('occupancyRateRow.spaces')}</span>
                </TableCell>
                <TableCell className="px-2 py-2 align-top">
                  {t('occupancyRateRow.percentage', { value: toNumberString(building.occupancyRate) })}
                </TableCell>
                <TableCell className="px-2 py-2 align-top">
                  {t('occupancyRateRow.percentage', { value: toNumberString(building.previousYearOccupancyRate ?? 0) })}
                </TableCell>
                <TableCell className="px-2 py-2 align-top">
                  <ChangePercentage value={building.occupancyChangePercentage ?? 0} />
                </TableCell>
              </TableRow>
              {expandedRows.has(building.id) && (
                <TableRow className="bg-surface-base">
                  <TableCell colSpan={columns.length + 1} className="p-0">
                    <ExpandedSpaceTable
                      spaces={building.children}
                      t={t}
                      tType={tType}
                    />
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          ))}
        </TableBody>
      )}
    />
  );
};

const ExpandedSpaceTable: React.FC<{
  spaces: OccupancyRatesDetailDto[] | null | undefined;
  t: (key: string, options?: Record<string, unknown>) => string;
  tType: (key: string) => string;
}> = ({ spaces, t, tType }) => {
  const sorted = useMemo(() => sortChildrenByVacancy(spaces), [spaces]);

  if (sorted.length === 0) return null;

  return (
    <Table className="w-full">
      <TableHead className="bg-stone-700">
        <TableHeadRow>
          <TableHeadCell>{t('occupancyRateRow.areaName')}</TableHeadCell>
          <TableHeadCell>{t('occupancyRateRow.location')}</TableHeadCell>
          <TableHeadCell>{t('occupancyRateRow.assetClass')}</TableHeadCell>
          <TableHeadCell>{t('occupancyRateRow.areaM2')}</TableHeadCell>
          <TableHeadCell>{t('occupancyRateRow.tenant')}</TableHeadCell>
          <TableHeadCell>{t('occupancyRateRow.leaseStart')}</TableHeadCell>
          <TableHeadCell>{t('occupancyRateRow.leaseEnd')}</TableHeadCell>
          <TableHeadCell>{t('occupancyRateRow.status')}</TableHeadCell>
        </TableHeadRow>
      </TableHead>
      <TableBody>
        {sorted.map((space) => (
          <TableRow key={space.id} className="border-b border-surface-elevated">
            <TableCell className="px-2 py-2 font-semibold">{space.name}</TableCell>
            <TableCell className="px-2 py-2">{space.parentName ?? '-'}</TableCell>
            <TableCell className="px-2 py-2">
              {space.assetClass ? tType(`AssetClass.${space.assetClass}`) : '-'}
            </TableCell>
            <TableCell className="px-2 py-2">
              {space.m2 != null ? `${toNumberString(space.m2)} m²` : '-'}
            </TableCell>
            <TableCell className="px-2 py-2">{space.tenantName ?? '-'}</TableCell>
            <TableCell className="px-2 py-2">
              {space.startDate ? dayjs(space.startDate).format('DD.MM.YYYY') : '-'}
            </TableCell>
            <TableCell className="px-2 py-2">
              {space.endDate ? dayjs(space.endDate).format('DD.MM.YYYY') : '-'}
            </TableCell>
            <TableCell className="px-2 py-2">
              <span
                style={
                  space.vacancyStatus === 'Rented'
                    ? { color: '#10b981' }
                    : space.vacancyStatus === 'Vacant'
                      ? { color: '#ef4444' }
                      : space.vacancyStatus === 'VacantSoon'
                        ? { color: '#f59e0b' }
                        : undefined
                }
              >
                {space.vacancyStatus ? tType(`VacancyStatus.${space.vacancyStatus}`) : '-'}
              </span>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
